import type { ComboBoxOption } from "@typings/components/comboBox";

export const validatePositiveInteger = () => (val: string | undefined) => {
  const num = Number(val);
  return !isNaN(num) && num >= 1 && Number.isInteger(num);
};

export const validatePositiveNumber = () => (val: string | undefined) => {
  const num = Number(val);
  return !isNaN(num) && num > 0;
};

export const validateLatitude = () => (val: string | undefined) => {
  const num = Number(val);
  return !isNaN(num) && num >= -90 && num <= 90;
};

export const validateLongitude = () => (val: string | undefined) => {
  const num = Number(val);
  return !isNaN(num) && num >= -180 && num <= 180;
};

export function validateCoordinates(latitude?: string, longitude?: string) {
  const lat = parseFloat(latitude || "");
  const lng = parseFloat(longitude || "");

  const hasValidCoordinates =
    !isNaN(lat) &&
    !isNaN(lng) &&
    validateLatitude()(latitude || "") &&
    validateLongitude()(longitude || "");

  return { hasValidCoordinates, lat, lng };
}

export const formatOption = (option: ComboBoxOption | null) => ({
  label: option?.label ?? "",
  value: String(option?.value ?? ""),
  item: option?.item,
});

export const isEmpty = (val: unknown) =>
  val === null ||
  val === undefined ||
  val === "" ||
  (Array.isArray(val) && val.length === 0) ||
  (typeof val === "object" && val !== null && Object.keys(val).length === 0);

export const actualDateTime = (): string =>
  new Date().toISOString().replace("T", "_").slice(0, 19).replace(/:/g, "-");

export const formatDateTimeForAPI = (dateTimeLocal: string): string => {
  if (!dateTimeLocal) return "";
  return new Date(dateTimeLocal).toISOString();
};

export function formatCoordinates(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): string | null {
  if (latitude == null || longitude == null) return null;

  const formatCoordinate = (coord: number): string => {
    const decimals = coord.toString().split(".")[1]?.length || 0;
    return decimals > 6 ? coord.toFixed(6) : coord.toString();
  };

  const lat = formatCoordinate(latitude);
  const lon = formatCoordinate(longitude);

  return `Lat: ${lat}, Long: ${lon}`;
}

export function formatDate(date: string | Date | null | undefined): string {
  if (isEmpty(date)) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date!;

  const formatter = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(dateObj);
}

export function formatDateTime(date: string | Date | null | undefined): string {
  if (isEmpty(date)) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date!;

  return dateObj.toLocaleString("pt-BR", {
    timeZone: "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDateTimeForUpdate(
  date: string | Date | null | undefined,
): string {
  if (!date) return "";

  const d = new Date(date);

  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const hours = String(d.getUTCHours()).padStart(2, "0");
  const minutes = String(d.getUTCMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function formatDateForUpdate(
  date: string | Date | null | undefined,
): string {
  if (!date) return "";

  const d = new Date(date);

  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}
